<script>
    import { onMount } from "svelte";

    import { fly } from "svelte/transition";

    import { apiMethods } from "../../Services/index.svelte";

    import { isMobile, user } from "../../stores";

    import {
        SearchInput,
        SvelteTable,
        PageLoading,
        Button,
        ResetFilterLink,
        SearchSelect,
    } from "../../Components/Common/index.svelte";

    import {
        getAmountInCurrency,
        getNumberWithComma,
        getFormatDate,
        getFormatDateAndTime,
        getText,
        getTitle,
        goToURL,
        handleGenerateAndDownloadCSV,
        replaceURL,
        showError,
        decodeSearchParams,
        encodeSearchParams,
        getDate,
        getDaysInMonth,
        getQuarterRangeByDate,
    } from "../../Utils/utilFunctions.svelte";

    import TableFooter from "../../Components/Layout/Table/TableFooter.svelte";
    import SearchDateRangeInput from "../../Components/Common/SearchDateRangeInput.svelte";

    export let params = {};

    $: if ($user.role == 100)
        columns.splice(2, 0, {
            key: "group",
            title: "Group",
            headerClass: "text-left pl-4 text-[14px] cursor-default",
            class: (v) =>
                defaultColumnClass +
                "cursor-default" +
                _getBgColorByLastInteractDate(v.lastInteraction),
            value: (v) =>
                v.groupName && v.groupId ? getText(v.groupName) : "-",
        });

    let isGettingAccountUsage = false,
        returnAccountUsageArr = [],
        isExportingCSVFile = false;

    let filters = { interval: Number(1) },
        showFilters = false;

    let page = 0,
        pageSize = 25,
        count;

    const defaultColumnClass = "text-left pl-4 text-[14px] ";

    const sortableHeaderKeys = [
        "id",
        "name",
        "createdDate",
        "totalEncounters",
        "totalBilledAmount",
        "totalCollectedAmount",
        "lastActivity",
    ];

    const columns = [
        {
            key: "id",
            title: "Doctor ID",
            headerClass:
                "text-left pl-4 text-[14px] min-w-[100px] sortable-header-decoration rounded-tl-xl",
            class: (v) =>
                defaultColumnClass +
                "cursor-default" +
                _getBgColorByLastInteractDate(v.lastInteraction),
            renderValue: (v) => getText(v.id),
        },
        {
            key: "name",
            title: "Name",
            value: (v) => _getFullName(v),
            headerClass:
                "text-left pl-4 text-[14px] sortable-header-decoration",
            class: (v) =>
                defaultColumnClass +
                "cursor-pointer hover:underline" +
                _getBgColorByLastInteractDate(v.lastInteraction),
        },
        {
            key: "createdDate",
            title: "Account Created",
            value: (v) =>
                v.accountCreatedDate
                    ? getFormatDate(v.accountCreatedDate)
                    : "-",
            headerClass:
                "text-left pl-4 text-[14px] sortable-header-decoration",
            class: (v) =>
                defaultColumnClass +
                "cursor-default" +
                _getBgColorByLastInteractDate(v.lastInteraction),
        },
        {
            key: "totalEncounters",
            title: "No. of Encounters",
            value: (v) =>
                v.totalEncounters ? getNumberWithComma(v.totalEncounters) : "0",
            headerClass:
                "text-left pl-4 text-[14px] sortable-header-decoration",
            class: (v) =>
                defaultColumnClass +
                "cursor-default" +
                _getBgColorByLastInteractDate(v.lastInteraction),
        },
        {
            key: "totalBilledAmount",
            title: "Total Billed",
            value: (v) => getAmountInCurrency(v.totalBilledAmount),
            headerClass:
                "text-left pl-4 text-[14px] sortable-header-decoration",
            class: (v) =>
                defaultColumnClass +
                "cursor-default" +
                _getBgColorByLastInteractDate(v.lastInteraction),
        },
        {
            key: "totalCollectedAmount",
            title: "Total Collected",
            value: (v) => getAmountInCurrency(v.totalCollectedAmount),
            headerClass:
                "text-left pl-4 text-[14px] sortable-header-decoration",
            class: (v) =>
                defaultColumnClass +
                "cursor-default" +
                _getBgColorByLastInteractDate(v.lastInteraction),
        },
        {
            key: "totalRefunded",
            title: "Total Refunded",
            value: (v) => getAmountInCurrency(v.totalRefundedAmount),
            headerClass: "text-left pl-4 text-[14px]",
            class: (v) =>
                defaultColumnClass +
                "cursor-default" +
                _getBgColorByLastInteractDate(v.lastInteraction),
        },
        {
            key: "lastActivity",
            title: "Last Interaction",
            value: (v) => getFormatDateAndTime(v.lastInteraction),
            headerClass:
                "text-left pl-4 text-[14px] sortable-header-decoration rounded-tr-xl",
            class: (v) =>
                defaultColumnClass +
                "cursor-default" +
                _getBgColorByLastInteractDate(v.lastInteraction),
        },
    ];

    const intervals = [
        { id: 1, type: "This month" },
        { id: 2, type: "This quarter" },
        { id: 3, type: "This year" },
        { id: 4, type: "Last month" },
        { id: 5, type: "Last quarter" },
        { id: 6, type: "Last year" },
        { id: 7, type: "Specific dates" },
    ];

    onMount(() => {
        if (params.query) updateSearchParamsByQuery(params.query);

        handleIntervalSelect(false);
        loadAccountUsage(page * pageSize, pageSize, _getSearchParams());
    });

    const updateSearchParamsByQuery = (query) => {
        const arr = decodeSearchParams(query).split("&");

        if (arr.length) {
            for (let i = 0; i < arr.length; i++) {
                if (arr[i].includes("page="))
                    page = Number(arr[i].split("=")[1]);

                if (arr[i].includes("size="))
                    pageSize = Number(arr[i].split("=")[1]);

                if (arr[i].includes("sort="))
                    filters.sort = arr[i].split("=")[1];

                if (arr[i].includes("search="))
                    filters.search = arr[i].split("=")[1];

                if (arr[i].includes("startDate="))
                    filters.startDate = arr[i].split("=")[1];

                if (arr[i].includes("endDate="))
                    filters.endDate = arr[i].split("=")[1];

                if (arr[i].includes("dateTimeInterval="))
                    filters.interval = Number(arr[i].split("=")[1]);
            }
            updateURL();
        }
    };

    const loadAccountUsage = async (start, limit, params) => {
        isGettingAccountUsage = true;

        await apiMethods.getMethod
            .getAccountUsage(start, limit, params)
            .then((res) => _handleAccountUsage(res))
            .then(() => _updateHeaderSortIcon());

        isGettingAccountUsage = false;
    };

    const handleFilterSelect = () => {
        page = 0;

        updateURL();

        loadAccountUsage(0, pageSize, _getSearchParams());
    };

    const handleIntervalSelect = (loadURL = true) => {
        const today = new Date();
        const dateFrom = new Date();
        const dateTo = new Date();

        switch (filters.interval) {
            case 1:
                dateFrom.setDate(1);
                break;
            case 2:
                const { start } = getQuarterRangeByDate(today);
                dateFrom.setFullYear(start.getFullYear());
                dateFrom.setMonth(start.getMonth());
                dateFrom.setDate(start.getDate());
                break;
            case 3:
                dateFrom.setMonth(0);
                dateFrom.setDate(1);
                break;
            case 4:
                dateFrom.setMonth(today.getMonth() - 1);
                dateFrom.setDate(1);
                dateTo.setMonth(today.getMonth() - 1);
                dateTo.setDate(
                    getDaysInMonth(dateTo.getFullYear(), dateTo.getMonth()),
                );
                break;
            case 5:
                const { start: prevStart, end: prevEnd } =
                    getQuarterRangeByDate(today, -1);
                dateFrom.setFullYear(prevStart.getFullYear());
                dateFrom.setMonth(prevStart.getMonth());
                dateFrom.setDate(prevStart.getDate());
                dateTo.setFullYear(prevEnd.getFullYear());
                dateTo.setMonth(prevEnd.getMonth());
                dateTo.setDate(prevEnd.getDate());
                break;
            case 6:
                dateFrom.setFullYear(today.getFullYear() - 1);
                dateFrom.setMonth(0);
                dateFrom.setDate(1);
                dateTo.setFullYear(today.getFullYear() - 1);
                dateTo.setMonth(11);
                dateTo.setDate(
                    getDaysInMonth(dateTo.getFullYear(), dateTo.getMonth()),
                );
                break;
            case 7:
            default:
                break;
        }

        if (filters.interval < 7) {
            filters.startDate = getDate(dateFrom, "-", true);
            filters.endDate = getDate(dateTo, "-", true);
        }

        updateURL();

        if (loadURL && filters.startDate && filters.endDate)
            loadAccountUsage(page * pageSize, pageSize, _getSearchParams());
    };

    const updateURL = () => {
        replaceURL(
            `/AccountUsage/page=${page}&size=${pageSize}${_getParamsString()}`,
        );
    };

    const toggleFilters = () => {
        showFilters = !showFilters;
    };

    const filterTransition = (node, args) => fly(node, args);

    const exportTableCsv = () => {
        isExportingCSVFile = true;

        handleGenerateAndDownloadCSV(
            _updateCSVContent(returnAccountUsageArr),
            "Doctors Usage",
        );

        isExportingCSVFile = false;
    };

    const onPageSizeChange = (newPageSize) => {
        pageSize = newPageSize;
        page = 0;

        updateURL();

        loadAccountUsage(0, newPageSize, _getSearchParams());
    };

    const onPageChange = (e) => {
        page = Number(e.detail.page);

        updateURL();

        loadAccountUsage(
            Number(e.detail.firstDataIndex),
            pageSize,
            _getSearchParams(),
        );
    };

    const handleClickCell = (
        { target: { className, classList } },
        rowData,
        key,
    ) => {
        if (key == "name") goToURL(`/Account/view/${rowData.id}`);
    };

    const handleClickCol = ({ target: { className } }, colData, key) => {
        if (sortableHeaderKeys.includes(key)) {
            if (filters.sort?.toLowerCase() == key.toLowerCase())
                filters.sort = `${key.toLowerCase()}desc`;
            else filters.sort = key.toLowerCase();

            updateURL();

            loadAccountUsage(0, pageSize, _getSearchParams());
        }
    };

    // HELPER FUNCTIONS
    const _handleAccountUsage = async (res) => {
        if (res.error) {
            showError(res.error);
            returnAccountUsageArr = [];
            count = 0;
        } else {
            returnAccountUsageArr = [...res.results];
            count = res.total;
        }
    };

    const _updateHeaderSortIcon = () => {
        let thRow = document.getElementsByTagName("th");
        const params = _getSearchParams();

        if (params) {
            setTimeout(() => {
                const upSpan = document.createElement("span");
                const downSpan = document.createElement("span");
                upSpan.innerHTML = `<i class="fas fa-sort-alpha-down" />`;
                upSpan.style = "font-size: 10px";
                downSpan.innerHTML = `<i class="fas fa-sort-alpha-up-alt" />`;
                downSpan.style = "font-size: 10px";

                Array.from(thRow).forEach((row) => {
                    if (
                        (params.sort == "id" &&
                            row.childNodes[0].data == "Doctor ID") ||
                        (params.sort == "name" &&
                            row.childNodes[0].data == "Name") ||
                        (params.sort == "createddate" &&
                            row.childNodes[0].data == "Account Created") ||
                        (params.sort == "totalencounters" &&
                            row.childNodes[0].data == "No. of Encounters") ||
                        (params.sort == "totalbilledamount" &&
                            row.childNodes[0].data == "Total Billed") ||
                        (params.sort == "totalcollectedamount" &&
                            row.childNodes[0].data == "Total Collected") ||
                        (params.sort == "lastactivity" &&
                            row.childNodes[0].data == "Last Interaction")
                    )
                        row.appendChild(upSpan);
                    else if (
                        (params.sort == "iddesc" &&
                            row.childNodes[0].data == "Doctor ID") ||
                        (params.sort == "namedesc" &&
                            row.childNodes[0].data == "Name") ||
                        (params.sort == "createddatedesc" &&
                            row.childNodes[0].data == "Account Created") ||
                        (params.sort == "totalencountersdesc" &&
                            row.childNodes[0].data == "No. of Encounters") ||
                        (params.sort == "totalbilledamountdesc" &&
                            row.childNodes[0].data == "Total Billed") ||
                        (params.sort == "totalcollectedamountdesc" &&
                            row.childNodes[0].data == "Total Collected") ||
                        (params.sort == "lastactivitydesc" &&
                            row.childNodes[0].data == "Last Interaction")
                    )
                        row.appendChild(downSpan);
                });
            }, 0);
        }
    };

    const _getSearchParams = () => {
        let params = { sort: "createddateDesc" };

        if (filters.sort) params.sort = filters.sort;

        if (filters.search) params.search = filters.search;

        if (filters.startDate) params["filter[startdate]"] = filters.startDate;

        if (filters.endDate) params["filter[enddate]"] = filters.endDate;

        return params;
    };

    const _getParamsString = () => {
        let str = "";
        const params = _getSearchParams();

        if (params) {
            if (params.sort) str += `&sort=${params.sort}`;

            if (params.search) str += `&search=${params.search}`;

            if (params["filter[startdate]"])
                str += `&startDate=${params["filter[startdate]"]}`;

            if (params["filter[enddate]"])
                str += `&endDate=${params["filter[enddate]"]}`;
        }

        if (filters.interval) str += `&dateTimeInterval=${filters.interval}`;

        return encodeSearchParams(str);
    };

    const _getFullName = (v) => {
        return `${getTitle(v.title)} ${v.firstName}${
            v.middleName ? " " + v.middleName : ""
        }${v.lastName ? " " + v.lastName : ""}`;
    };
    const _updateCSVContent = (arr) => {
        if (!arr.length) return [];

        const results = [];

        arr.map((result) => {
            results.push({
                "Doctor ID": getText(result.id),
                Name: _getFullName(result),
                "Account Created": result.accountCreatedDate
                    ? getFormatDate(result.accountCreatedDate)
                    : "-",
                "No. of Encounters": result.totalEncounters
                    ? getText(result.totalEncounters)
                    : "0",
                "Total Billed": getAmountInCurrency(
                    result.totalBilledAmount,
                ).replaceAll(",", ""),
                "Total Collected": getAmountInCurrency(
                    result.totalCollectedAmount,
                ).replaceAll(",", ""),
                "Total Refunded": getAmountInCurrency(
                    result.totalRefundedAmount,
                ).replaceAll(",", ""),
                "Last Interaction": getFormatDateAndTime(
                    result.lastInteraction,
                ).replace(",", " "),
            });
        });

        return results;
    };

    const _getBgColorByLastInteractDate = (date) => {
        if (!date) return "";

        const daysDiff = (new Date() - new Date(date)) / (1000 * 60 * 60 * 24);

        if (daysDiff < 14) return "";
        else if (daysDiff < 28) return " " + "bg-amber-100";
        else return " " + "bg-red-100";
    };
</script>

<div class="flex flex-col flex-1 gap-y-4">
    {#if $isMobile}
        <button
            class="btn btn-accent btn-sm w-32 flex flex-row gap-x-1"
            on:click|preventDefault={toggleFilters}
        >
            Filters
            <i class="fa fa-plus mr-2 text-[14px]" aria-hidden="true" />
            <i
                style="font-size:14px"
                class={showFilters ? "fa arrow-up" : "fa arrow-down"}
                >&#xf104;
            </i>
        </button>
    {/if}

    {#if !$isMobile || showFilters}
        <div
            class="flex flex-0 items-start {$isMobile
                ? 'flex-col-reverse gap-y-4'
                : 'justify-between'}"
            in:filterTransition={{ duration: 200, y: -50 }}
        >
            <div class="flex gap-4 flex-wrap items-end">
                <SearchInput
                    bind:value={filters.search}
                    title="Search"
                    inputFunc={handleFilterSelect}
                    isLong
                />

                <SearchSelect
                    bind:value={filters.interval}
                    title="Date filter"
                    onChangeFunc={handleIntervalSelect}
                    isDisabled={isGettingAccountUsage}
                >
                    {#each intervals as interval}
                        <option value={interval.id}>
                            {interval.type}
                        </option>
                    {/each}
                </SearchSelect>

                {#if !$user.settings?.disableDateRangeInput}
                    <SearchDateRangeInput
                        bind:startDate={filters.startDate}
                        bind:endDate={filters.endDate}
                        title="Date Range"
                        inputClass="borderB w-[272px]"
                        on:change={handleFilterSelect}
                        showClearButton
                        disable={isGettingAccountUsage}
                        showScrollYearOptions
                    />
                {:else}
                    <SearchInput
                        bind:value={filters.startDate}
                        title="Date from"
                        type="date"
                        isLong
                        inputClass="borderB"
                        inputFunc={handleFilterSelect}
                        isDisable={isGettingAccountUsage}
                        maxDate={filters.endDate ?? undefined}
                        showScrollYearOptions
                    />

                    <SearchInput
                        bind:value={filters.endDate}
                        title="Date to"
                        type="date"
                        isLong
                        inputClass="borderB"
                        inputFunc={handleFilterSelect}
                        isDisable={isGettingAccountUsage}
                        minDate={filters.startDate ?? undefined}
                        showScrollYearOptions
                    />
                {/if}

                <ResetFilterLink
                    bind:filter={filters}
                    defaultFilter={{ sort: "createddateDesc" }}
                    onResetFilter={() => {
                        handleIntervalSelect(false);
                        handleFilterSelect();
                    }}
                />
            </div>

            <!-- <Button
                isDisabled={!returnAccountUsageArr.length || isExportingCSVFile}
                clickFunc={exportTableCsv}
                manualClass={isExportingCSVFile ? "loading" : ""}
            >
                {isExportingCSVFile ? "Exporting" : "Export CSV"}
            </Button> -->
        </div>
    {/if}

    <div class={$isMobile ? "h-auto w-minus48" : "relative flex-1"}>
        {#if isGettingAccountUsage}
            <PageLoading />
        {:else}
            <div
                class={$isMobile
                    ? "overflow-x-auto overflow-y-visible"
                    : "overflow-y-auto absolute inset-0"}
            >
                <SvelteTable
                    {columns}
                    rows={returnAccountUsageArr}
                    clickCellFunc={({ detail: { event, row, key } }) => {
                        handleClickCell(event, row, key);
                    }}
                    clickColFunc={({ detail: { event, row, key } }) => {
                        handleClickCol(event, row, key);
                    }}
                />
            </div>
        {/if}
    </div>

    <TableFooter
        {page}
        {pageSize}
        {count}
        tableData={returnAccountUsageArr}
        {onPageChange}
        {onPageSizeChange}
        csvConfig={{
            columns,
            data: returnAccountUsageArr,
            fileName: "Usage",
            allDataApi: async () =>
                await apiMethods.getMethod.getAccountUsage(
                    0,
                    999999,
                    _getSearchParams(),
                ),
        }}
    />
</div>
